import { useImperativeHandle, forwardRef } from 'react';
import { 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Radio, 
  Checkbox, 
  Switch, 
  DatePicker, 
  Upload, 
  Button,
  Row,
  Col
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';
import type { FormField, FormConfig, FormCallbacks } from '../../types/form';

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

export interface GlobalFormRef {
  form: FormInstance;
  submit: () => void;
  reset: () => void;
  getValues: () => any;
  setFieldsValue: (values: any) => void;
}

interface GlobalFormProps extends FormConfig, FormCallbacks {
  loading?: boolean;
  showSubmit?: boolean;
  showReset?: boolean;
  submitText?: string;
  resetText?: string;
}

/**
 * 全局通用表单组件
 * 支持通过配置动态生成表单字段
 */
const GlobalForm = forwardRef<GlobalFormRef, GlobalFormProps>((props, ref) => {
  const {
    fields,
    initialValues,
    layout = 'vertical',
    labelCol,
    wrapperCol,
    columns = 1,
    loading = false,
    showSubmit = true,
    showReset = false,
    submitText = '提交',
    resetText = '重置',
    onFinish,
    onFinishFailed,
    onValuesChange,
    onReset,
  } = props;

  const [form] = Form.useForm();

  // 暴露表单实例和方法
  useImperativeHandle(ref, () => ({
    form,
    submit: () => form.submit(),
    reset: () => form.resetFields(),
    getValues: () => form.getFieldsValue(),
    setFieldsValue: (values) => form.setFieldsValue(values),
  }));

  /**
   * 根据字段类型渲染对应的表单组件
   */
  const renderFormControl = (field: FormField) => {
    if (field.customRender) {
      return field.customRender();
    }

    const commonProps = {
      placeholder: field.placeholder,
      disabled: field.disabled,
      size: 'large' as const,
    };

    switch (field.type) {
      case 'input':
        return <Input {...commonProps} maxLength={field.max} />;

      case 'textarea':
        return (
          <TextArea 
            {...commonProps} 
            rows={field.rows || 4} 
            maxLength={field.max}
            showCount
          />
        );

      case 'number':
        return (
          <InputNumber 
            {...commonProps} 
            min={field.min} 
            max={field.max}
            style={{ width: '100%' }}
          />
        );

      case 'password':
        return <Input.Password {...commonProps} maxLength={field.max} />;

      case 'select':
        return (
          <Select 
            {...commonProps}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {field.options?.map((option) => (
              <Option 
                key={String(option.value)} 
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </Option>
            ))}
          </Select>
        );

      case 'multiSelect':
        return (
          <Select 
            {...commonProps}
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {field.options?.map((option) => (
              <Option 
                key={String(option.value)} 
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </Option>
            ))}
          </Select>
        );

      case 'radio':
        return (
          <Radio.Group disabled={field.disabled}>
            {field.options?.map((option) => (
              <Radio 
                key={String(option.value)} 
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        );

      case 'checkbox':
        return (
          <Checkbox.Group 
            options={field.options?.map(opt => ({
              label: opt.label,
              value: opt.value,
              disabled: opt.disabled,
            }))}
            disabled={field.disabled}
          />
        );

      case 'switch':
        return <Switch disabled={field.disabled} />;

      case 'date':
        return (
          <DatePicker 
            {...commonProps}
            showTime={field.showTime}
            format={field.format || (field.showTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD')}
            style={{ width: '100%' }}
          />
        );

      case 'dateRange':
        return (
          <RangePicker 
            disabled={field.disabled}
            size="large"
            showTime={field.showTime}
            format={field.format || (field.showTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD')}
            style={{ width: '100%' }}
          />
        );

      case 'time':
        return (
          <DatePicker.TimePicker 
            {...commonProps}
            format={field.format || 'HH:mm:ss'}
            style={{ width: '100%' }}
          />
        );

      case 'upload':
        return (
          <Upload
            maxCount={field.maxCount}
            accept={field.accept}
            multiple={field.multiple}
          >
            <Button icon={<UploadOutlined />}>点击上传</Button>
          </Upload>
        );

      default:
        return <Input {...commonProps} />;
    }
  };

  /**
   * 渲染表单项
   */
  const renderFormItem = (field: FormField, index: number) => {
    if (field.hidden) {
      return null;
    }

    const colSpan = field.colSpan || (24 / columns);

    // Switch 组件需要使用 checked 属性而非 value
    const valuePropName = field.type === 'switch' ? 'checked' : undefined;

    return (
      <Col span={colSpan} key={index}>
        <Form.Item
          name={field.name}
          label={field.label}
          rules={field.rules}
          tooltip={field.tooltip}
          dependencies={field.dependencies}
          extra={field.extra}
          initialValue={field.defaultValue}
          valuePropName={valuePropName}
        >
          {renderFormControl(field)}
        </Form.Item>
      </Col>
    );
  };

  return (
    <Form
      form={form}
      layout={layout}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
      initialValues={initialValues}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={onValuesChange}
      autoComplete="off"
    >
      <Row gutter={16}>
        {fields.map((field, index) => renderFormItem(field, index))}
      </Row>

      {(showSubmit || showReset) && (
        <Form.Item style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {showSubmit && (
              <Button type="primary" htmlType="submit" loading={loading}>
                {submitText}
              </Button>
            )}
            {showReset && (
              <Button 
                onClick={() => {
                  form.resetFields();
                  onReset?.();
                }}
              >
                {resetText}
              </Button>
            )}
          </div>
        </Form.Item>
      )}
      
    </Form>
  );
});

GlobalForm.displayName = 'GlobalForm';

export default GlobalForm;

